import { useEffect, useState } from "react";
import PageHeading from "../../components/PageElements/PageHeading";

import Table from "../../components/Tables/Table";
import { Form, Modal, Space, Tag, Tooltip } from "antd";
import { FiEdit, FiTrash2 } from "react-icons/fi";
import toast from "react-hot-toast";
import TableHeader from "../../components/Tables/TableHeader";
import { categorySchema } from "../../validators/categoryValidators";
import TextInput from "../../components/Inputs/TextInput";
import FormButton from "../../components/Buttons/FormButton";
import { Formik } from "formik";
import FormCancelButton from "../../components/Buttons/FormCancelButton";
import FormErrors from "../../components/Errors/FormErrors";
import DeleteModal from "../../components/Modals/DeleteModal";
import { IoIosWarning } from "react-icons/io";
import {
  createProgramLevel,
  deleteProgramLevel,
  getProgramLevels,
  updateProgramLevel,
} from "../../services/ProgramLevel";

const ProgramLevel = () => {
  const [programLevels, setProgramLevels] = useState([]);
  const [filteredProgramLevels, setFilteredProgramLevels] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [programLevelToEdit, setProgramLevelToEdit] = useState(null);
  const [modalText, setModalText] = useState("");
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [programLevelToDelete, setProgramLevelToDelete] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [errs, setErrs] = useState(null);

  useEffect(() => {
    loadProgramLevels();
  }, []);

  useEffect(() => {
    const debounce = setTimeout(() => {
      const filtered = programLevels.filter((item) =>
        item.name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredProgramLevels(filtered);
    }, 300);

    return () => clearTimeout(debounce);
  }, [searchTerm, programLevels]);

  const loadProgramLevels = async () => {
    setLoading(true);
    try {
      const { data } = await getProgramLevels();
      setProgramLevels(data.data);
      setFilteredProgramLevels(data.data);
    } catch (error) {
      toast.error(error?.response?.data?.error || "Failed to load categories");
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = (payload, resetForm) => {
    if (programLevelToEdit) {
      onUpdateProgramLevel(payload, resetForm);
    } else {
      onCreateProgramLevel(payload, resetForm);
    }
  };

  const onCreateProgramLevel = async (payload, resetForm) => {
    setErrs(null);

    try {
      const { data } = await createProgramLevel(payload);

      setProgramLevels((prev) => [...prev, data.data]);

      toast.success("Program Level created successfully");

      setIsModalOpen(false);

      resetForm();
    } catch (error) {
      const { response } = error;
      if (response && response.status === 422) {
        setErrs(response.data.errors);
      } else {
        toast.error(
          response.data.error || "Program Level was not created, try again!"
        );
      }
    }
  };

  const handleEdit = (category) => {
    setProgramLevelToEdit(category);
    setIsModalOpen(true);
  };

  const onUpdateProgramLevel = async (payload, resetForm) => {
    setErrs(null);

    try {
      const originalProgramLevels = [...programLevels];

      const programLevelIndex = originalProgramLevels.findIndex(
        (category) => category.id === programLevelToEdit.id
      );

      if (programLevelIndex === -1) throw new Error("Program Level not found.");

      const { data } = await updateProgramLevel(programLevelToEdit.id, payload);

      originalProgramLevels[programLevelIndex] = data.data;

      setProgramLevels(originalProgramLevels);

      setProgramLevelToEdit(null);

      resetForm();

      setIsModalOpen(false);

      toast.success("Program Level updated successfully!");
    } catch (error) {
      const { response } = error;
      if (response && response.status === 422) {
        setErrs(response.data.errors);
      } else {
        toast.error(
          response?.data?.error || "Program Level not updated, try again!"
        );
      }
    }
  };

  const handleDelete = (payload) => {
    setModalText(
      `Please confirm that you want to delete %${payload?.name?.toUpperCase()}`
    );
    setProgramLevelToDelete(payload);
    setIsDeleteModalOpen(true);
  };

  const onDeleteProgramLevel = async () => {
    const originalProgramLevels = [...programLevels];

    const newProgramLevels = originalProgramLevels.filter(
      (programLevel) => programLevel.id !== programLevelToDelete.id
    );

    setProgramLevels(newProgramLevels);

    try {
      await deleteProgramLevel(programLevelToDelete?.id);

      toast.success("Program Level deleted successfully!");
    } catch (error) {
      const { response } = error;
      toast.error(
        ` ${
          response?.data?.message || error?.response?.data?.error ||
          "Program Level not deleted, try again later"
        }`
      );

      setProgramLevels(originalProgramLevels);
    } finally {
      setIsDeleteModalOpen(false);

      setProgramLevelToDelete(null);
    }
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setIsDeleteModalOpen(false);
    setProgramLevelToEdit(null);
    setProgramLevelToDelete(null);
    setErrs(null);
  };

  const columns = [
    {
      title: "#",
      render: (text, record, index) => index + 1,
      width: 300,
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      render: (text) => {
        const isEmpty = !text?.trim();
        const displayText = isEmpty ? "N/A" : text;

        return isEmpty ? (
          <div style={{ textAlign: "left", color: "#888" }}>{displayText}</div>
        ) : (
          <Tooltip title={text}>
            <div
              style={{
                maxWidth: 250,
                overflow: "hidden",
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
              }}
            >
              {text}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: "Action",
      key: "action",
      width: 160,
      render: (_, record) => (
        <Space size="middle">
          <Tooltip title="Edit Program Level">
            <button
              className="p-2 border border-green-200 rounded-md text-green-600 hover:bg-green-50 transition cursor-pointer"
              onClick={() => handleEdit(record)}
            >
              <FiEdit size={16} />
            </button>
          </Tooltip>
          <Tooltip title="Delete Program Level">
            <button
              className="p-2 border border-red-200 rounded-md text-red-500 hover:bg-red-50 transition cursor-pointer"
              onClick={() => handleDelete(record)}
            >
              <FiTrash2 size={16} />
            </button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <>
      <PageHeading title="Program Levels" subtitle1="Program Levels" />

      <div className="px-8 py-6">
        <div className="bg-white shadow-sm rounded-lg p-6">
          <TableHeader
            title="Program Level List"
            count={filteredProgramLevels.length}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            buttonLabel="+ Add Program Level"
            onButtonClick={() => setIsModalOpen(true)}
          />

          <Table
            isPaginable
            pageSize={10}
            isSearchable={false}
            loading={loading}
            columns={columns}
            rows={filteredProgramLevels.map((item) => ({
              ...item,
              key: item.id,
            }))}
          />

          {/* Modal */}

          <Modal
            title={
              <div className="px-4 py-3">
                <h2 className="text-lg font-semibold text-gray-700">
                  {programLevelToEdit
                    ? "Edit Program Level"
                    : "Add Program Level"}
                </h2>
              </div>
            }
            open={isModalOpen}
            onCancel={handleCancel}
            footer={false}
          >
            <Formik
              initialValues={{
                name: programLevelToEdit ? programLevelToEdit?.name : "",
                description: programLevelToEdit
                  ? programLevelToEdit?.description
                  : "",
              }}
              enableReinitialize
              validationSchema={categorySchema}
              onSubmit={(values, { resetForm }) => {
                onSubmit(values, resetForm);
              }}
            >
              {({ values, handleChange, handleBlur, errors, submitForm }) => {
                return (
                  <Form className="space-y-5 px-4! py-4!">
                    {errs && <FormErrors errors={errs} />}

                    <TextInput
                      name="name"
                      title="Name"
                      placeholder="Enter Program Level"
                      requiredField={true}
                      errors={errors}
                      values={values}
                      handleBlur={handleBlur}
                      handleChange={handleChange}
                      autoFocus
                    />

                    <TextInput
                      name="description"
                      title="Description"
                      placeholder="Enter Program Level Description"
                      errors={errors}
                      values={values}
                      handleBlur={handleBlur}
                      handleChange={handleChange}
                    />

                    <div className="flex justify-end gap-2">
                      <FormCancelButton text="Cancel" onClick={handleCancel} />
                      <FormButton
                        text="Save"
                        type="primary"
                        onClick={submitForm}
                      />
                    </div>
                  </Form>
                );
              }}
            </Formik>
          </Modal>

          <DeleteModal
            open={isDeleteModalOpen}
            handleOk={onDeleteProgramLevel}
            handleCancel={handleCancel}
            confirmLoading={confirmLoading}
            modalText={modalText}
            extra={
              <div className="mt-3 bg-red-100 text-red-500 border-[1px] border-red-300 mx-5 px-3 py-2 rounded-md">
                <h3 className="text-red-700 text-md flex gap-1 items-center">
                  <IoIosWarning /> Warning
                </h3>
                Please note that this action cannot be reversed!
              </div>
            }
          />
        </div>
      </div>
    </>
  );
};

export default ProgramLevel;
